import Badge from "@/components/atoms/Badge";
import Container from "@/components/atoms/Container";
import UserForm, {
  UserProps,
  useUserForm,
} from "@/components/organisms/User/Form";
import useAccess from "@/hooks/useAccess";
import User from "@/types/User";
import { Avatar, Button, Text } from "@radix-ui/themes";
import { PenSquare, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import * as Constants from "@/constants";
import { router } from "@inertiajs/react";
import useMessageProps from "@/hooks/useMessageProps";
import { Tabs, Box } from "@radix-ui/themes";
import CountriesTab from "@/components/organisms/User/CountriesTab";
import PermissionsTab from "@/components/organisms/User/PermissionsTab";
import StandardBreadcrumb from "@/components/molecules/StandardBreadcrumb";

export type UserPageProps = UserProps & {
  user: User;
  redirect_props: any;
};

const ShowUser = ({ user, redirect_props, ...rest }: UserPageProps) => {
  console.log(user);
  const form = useUserForm(user);
  const [loading, setLoading] = useState(false);
  const [isInfoTab, setIsInfoTab] = useState(true);
  const { getAccess } = useAccess();
  const [edit, setEdit] = useState(false);
  const tabRef = useRef(null);
  const [active, setActive] = useState("info");

  const breadcrumbItems = [
    {
      label: "Users",
      onClick: () => router.get("/admin/users"),
    },
    {
      label: user.name,
    },
  ];

  useMessageProps({ redirect_props });

  const onSubmit = (data: User) => {
    router.put(
      `/admin/users/${user.id}`,
      data,
      {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      }
    );
  };

  useEffect(() => {
    if (!loading && redirect_props?.success) {
      setEdit(false);
    }
  }, [loading]);

  useEffect(() => {
    if (tabRef?.current)
      [...(tabRef?.current as any).children]
        .find((el: any) => el.dataset.state === "active")
        .scrollIntoView({
          inline: "center",
        });
  }, [active]);

  return (
    <>
      <Container>
        <StandardBreadcrumb items={breadcrumbItems} />
        <div className="flex items-center justify-between mb-6 pt-2">
          <div className="flex items-center gap-2">
            <Avatar
              fallback={user.name.trim().charAt(0)}
              src={user?.avatar_url}
              variant="solid"
              radius="full"
            />
            <Text size="6">{user.name}</Text>
            <Badge
              variant={
                user.status === Constants.STATUSES.ACTIVE
                  ? "success"
                  : "destructive"
              }
            >
              {user.status}
            </Badge>
          </div>
          {isInfoTab && (
            <div>
              <Button
                onClick={() => setEdit(!edit)}
                disabled={!getAccess(["update_user"])}
              >
                {edit ? (
                  <>
                    <X size={16} className="mr-2" /> <span>Cancel</span>
                  </>
                ) : (
                  <>
                    <PenSquare size={16} className="mr-2" />
                    <span>Edit</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </Container>
      <Tabs.Root
        value={active}
        onValueChange={(tab) => {
          const isInfoTab = tab === "info";
          setIsInfoTab(isInfoTab);
          if (edit && isInfoTab) setEdit(false);
          setActive(tab);
        }}
        className="account-tabs"
      >
        <Tabs.List className={"px-2"} ref={tabRef}>
          <Tabs.Trigger value="info">Information</Tabs.Trigger>
          <Tabs.Trigger value="permissions">Permissions</Tabs.Trigger>
          <Tabs.Trigger value="countries">Countries</Tabs.Trigger>
        </Tabs.List>

        <Box>
          <Tabs.Content value="info">
            <Container className="py-4">
              <UserForm
                form={form}
                edit={edit}
                user={user}
                onSubmit={onSubmit}
                data={rest}
                loading={loading}
                errors={redirect_props?.errors}
              />
            </Container>
          </Tabs.Content>

          <Tabs.Content value="permissions">
            {getAccess(["update_user_permissions"]) ? (
                <PermissionsTab user={user} allPermissions={rest.all_permissions} />
              ) : (
                <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
                  You are not authorized to view this content.
                </span>
              )}
          </Tabs.Content>

          <Tabs.Content value="countries">
            {getAccess(["update_user_countries"]) ? (
              <CountriesTab user={user} countries={rest.all_countries} />
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
                You are not authorized to view this content.
              </span>
            )}
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </>
  );
};

export default ShowUser;
